import { ULID_LENGTH, ULID_MAX_TIMESTAMP_MS } from "./constants.js";
import { VALIDATION_ERROR_CODES, ValidationError } from "./errors.js";

/**
 * ULID implementation (Crockford base32, 128 bits: 48-bit time + 80-bit random).
 *
 * Determinism contract (AGENTS §1.3): this module never touches `Date.now` or
 * `Math.random`. Both are injected via {@link IdDependencies}, so tests and
 * record/replay harnesses produce byte-identical identifiers.
 */

const TIME_CHARS = 10;
const RANDOM_CHARS = 16;
const RANDOM_BYTES = 10;
const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

declare const ulidBrand: unique symbol;
declare const entityBrand: unique symbol;

/** A canonical, uppercase ULID string. */
export type Ulid = string & { readonly [ulidBrand]: true };

/** A ULID tagged with the entity kind it identifies (Poka-Yoke). */
export type EntityId<Kind extends string> = Ulid & { readonly [entityBrand]: Kind };

/** Injected time and randomness sources. */
export interface IdDependencies {
  /** Milliseconds since the Unix epoch, UTC. */
  readonly now: () => number;
  /** Cryptographically-secure random bytes of the requested length. */
  readonly randomBytes: (length: number) => Uint8Array;
}

function crockfordChar(index: number): string {
  return CROCKFORD_ALPHABET.charAt(index);
}

/** Encodes a 48-bit epoch-millisecond timestamp as 10 Crockford base32 chars. */
export function encodeTime(timestampMs: number): string {
  if (!Number.isInteger(timestampMs) || timestampMs < 0 || timestampMs > ULID_MAX_TIMESTAMP_MS) {
    throw new ValidationError(
      VALIDATION_ERROR_CODES.failed,
      `ULID timestamp out of range: ${timestampMs}`,
      { details: { timestampMs } },
    );
  }
  let remaining = timestampMs;
  let out = "";
  for (let i = 0; i < TIME_CHARS; i += 1) {
    out = crockfordChar(remaining % 32) + out;
    remaining = Math.floor(remaining / 32);
  }
  return out;
}

/** Encodes 80 bits of randomness as 16 Crockford base32 chars. */
export function encodeRandom(bytes: Uint8Array): string {
  if (bytes.length < RANDOM_BYTES) {
    throw new ValidationError(
      VALIDATION_ERROR_CODES.failed,
      `ULID requires at least ${RANDOM_BYTES} random bytes, received ${bytes.length}`,
    );
  }
  let out = "";
  for (let i = 0; i < RANDOM_CHARS; i += 1) {
    out += crockfordChar((bytes[i] ?? 0) % 32);
  }
  return out;
}

/** Creates a fresh ULID from injected dependencies. */
export function createUlid(deps: IdDependencies): Ulid {
  return (encodeTime(deps.now()) + encodeRandom(deps.randomBytes(RANDOM_BYTES))) as Ulid;
}

/** Creates a fresh ULID branded for a specific entity kind. */
export function createId<Kind extends string>(kind: Kind, deps: IdDependencies): EntityId<Kind> {
  void kind;
  return createUlid(deps) as EntityId<Kind>;
}

/** Type guard for canonical ULIDs. */
export function isUlid(value: string): value is Ulid {
  return value.length === ULID_LENGTH && ULID_PATTERN.test(value);
}

/** Asserts and brands a string as a ULID. */
export function assertUlid(value: string, field: string): Ulid {
  if (!isUlid(value)) {
    throw new ValidationError(VALIDATION_ERROR_CODES.failed, `Invalid ULID in ${field}`, {
      details: { field },
    });
  }
  return value;
}

/** Recovers the 48-bit epoch-millisecond timestamp encoded in a ULID. */
export function ulidTimestamp(ulid: Ulid): number {
  let timestamp = 0;
  for (let i = 0; i < TIME_CHARS; i += 1) {
    timestamp = timestamp * 32 + CROCKFORD_ALPHABET.indexOf(ulid.charAt(i));
  }
  return timestamp;
}

/* -------------------------------------------------------------------------- */
/* Entity identifiers                                                          */
/* -------------------------------------------------------------------------- */

export type UserId = EntityId<"UserId">;
export type SessionId = EntityId<"SessionId">;
export type PasskeyId = EntityId<"PasskeyId">;
export type DomainId = EntityId<"DomainId">;
export type MailboxId = EntityId<"MailboxId">;
export type AliasId = EntityId<"AliasId">;
export type MessageId = EntityId<"MessageId">;
export type ThreadId = EntityId<"ThreadId">;
export type LabelId = EntityId<"LabelId">;
export type AttachmentId = EntityId<"AttachmentId">;
export type DraftId = EntityId<"DraftId">;
export type ContactId = EntityId<"ContactId">;
export type RoutingRuleId = EntityId<"RoutingRuleId">;
export type AuditLogId = EntityId<"AuditLogId">;
export type BounceId = EntityId<"BounceId">;
export type SpamScoreId = EntityId<"SpamScoreId">;
export type DeliveryId = EntityId<"DeliveryId">;
export type ExportJobId = EntityId<"ExportJobId">;

/** All entity id kinds, used by the schema factory. */
export const ID_KINDS = [
  "UserId",
  "SessionId",
  "PasskeyId",
  "DomainId",
  "MailboxId",
  "AliasId",
  "MessageId",
  "ThreadId",
  "LabelId",
  "AttachmentId",
  "DraftId",
  "ContactId",
  "RoutingRuleId",
  "AuditLogId",
  "BounceId",
  "SpamScoreId",
  "DeliveryId",
  "ExportJobId",
] as const;
export type IdKind = (typeof ID_KINDS)[number];
